import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import api from "../../lib/api.ts";

const WishList = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Currency utils
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  // State
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [childBuyers, setChildBuyers] = useState([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const selectedChildBuyer = React.useMemo(() => {
    if (!selectedBuyerId) return null;
    return childBuyers.find((child) => child.buyerId === selectedBuyerId) || null;
  }, [childBuyers, selectedBuyerId]);

  const selectedBuyerDisplayName =
    selectedChildBuyer?.user?.name || user?.name || "";
  const selectedBuyerDisplayClientId =
    selectedChildBuyer?.user?.clientId || user?.clientId || "";
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Fetch child buyers (corporate buyer dropdown)
  useEffect(() => {
    const fetchChildBuyers = async () => {
      try {
        const response = await api.get("/users/corporatebuyers", {
          params: { limit: 100 },
        });
        const data = response.data?.data || [];
        setChildBuyers(data);
      } catch (error) {
        console.debug("Unable to load corporate child buyers:", error?.message);
        setChildBuyers([]);
      }
    };

    fetchChildBuyers();
  }, []);

  // Fetch wishlist items (server-side paginated)
  const fetchWishlistItems = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError("");

      const params = {
        page: currentPage,
        limit: 10,
      };

      if (selectedBuyerId) {
        params.buyerId = selectedBuyerId;
      }

      const response = await api.get("/wishlist", {
        params,
      });

      if (response.data.success) {
        setWishlistItems(response.data.data || []);
        console.log(response.data);

        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        setWishlistItems([]);
        setPagination({
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        });
      }
    } catch (err) {
      console.error("Error fetching wishlist:", err);
      setError(
        translate("wishList.messages.loadError") ||
        "Failed to load Watch List items. Please try again."
      );
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (vehicleId) => {
    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        // Remove from local state
        setWishlistItems((prev) =>
          prev.filter((item) => item.vehicle?.id !== vehicleId)
        );
        // Update total count in pagination
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          totalPages: Math.ceil((prev.total - 1) / prev.limit),
        }));
        showSuccess(translate("wishList.messages.removeSuccess") || "Removed from Watch List");
      } else {
        showError(translate("wishList.messages.removeError") || "Failed to remove item");
      }
    } catch (err) {
      console.error("Error removing from Watch List:", err);
      showError(translate("wishList.messages.removeError") || "Failed to remove item");
    }
  };

  // Load data
  useEffect(() => {
    fetchWishlistItems();
  }, [user?.id, currentPage, selectedBuyerId]);

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
  };

  console.log(wishlistItems.length, pagination.total);


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="bg-card rounded-[8px] sm:rounded-md shadow overflow-x-auto mx-0 sm:mx-1 md:mx-0"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-3 min-h-[72px]">
          {/* Heading */}
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap flex items-center gap-2">
              {translate("wishList.heading") || "My Watch List"}
            </h2>
            {/* {selectedBuyerDisplayName && (
              <p className="text-xs text-white/80 mt-1">
                {translate("wishList.labels.currentBuyer") || "Showing for"} {selectedBuyerDisplayName}
                {selectedBuyerDisplayClientId ? ` (${selectedBuyerDisplayClientId})` : ""}
              </p>
            )} */}
          </div>

          <div className="flex flex-col md:flex-row items-center gap-3 lg:ms-auto">
            {/* Child Buyer Dropdown */}
            {childBuyers.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={selectedBuyerId || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedBuyerId(val ? Number(val) : null);
                    setCurrentPage(1);
                  }}
                  className="bg-page p-2 rounded-full text-white text-xs focus:outline-none"
                >
                  {/* Default "All" option selected initially */}
                  <option value="">Select Buyer</option>
                  {childBuyers.map((child) => (
                    <option key={child.buyerId} value={child.buyerId}>
                      {child.user?.name || child.businessName || "-"}
                      {child.user?.clientId ? ` (${child.user.clientId})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Total Count */}
            <div className="px-4 py-1.5 rounded-full font-medium text-sm bg-white text-primary shadow-md whitespace-nowrap">
              {wishlistItems.length} {translate("wishList.pagination.items") || "items"}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 sm:p-5">
        {/* Error */}
        {error && (
          <div className="text-center py-8 text-red-600 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && wishlistItems.length === 0 && (
          <div className="text-center py-12">
            <Heart className="mx-auto h-16 w-16 text-gray-300 mb-4" strokeWidth={1.5} />
            <h3 className="text-lg font-medium text-white mb-2">
              {translate("wishList.emptyState.title") || "Your Watch List is empty"}
            </h3>
            <p className="text-description mb-6 max-w-md mx-auto">
              {translate("wishList.emptyState.description") ||
                "You haven't added any vehicles to your Watch List yet."}
            </p>
            <button
              onClick={() => navigate("/vehicles")}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors font-medium"
            >
              {translate("wishList.emptyState.browseButton") || "Browse Vehicles"}
            </button>
          </div>
        )}

        {/* Wishlist Items */}
        {wishlistItems.length > 0 && (
          <div className="space-y-2">
            {wishlistItems.map((item) => {
              const vehicle = item.vehicle || {};
              const imageSrc =
                vehicle?.images?.[0]?.imageUrl ||
                vehicle?.imageUrl ||
                placeholder;
              const isChildRecord = !!item.buyer?.parentId;

              return (
                <div
                  key={item.id || vehicle.id}
                  className={`border border-gray-800/50 rounded-xl shadow-sm overflow-hidden hover:shadow-lg hover:shadow-black/30 transition-all duration-200 ${isChildRecord ? "bg-gray-900/40" : "bg-page"
                    }`}
                >
                  <div className="p-4 sm:p-2.5">
                    <div className="flex flex-col lg:flex-row gap-5">
                      {/* Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={imageSrc}
                          alt={vehicle.name || "Vehicle"}
                          className={` lg:w-32 h-20 lg:h-20 object-cover rounded-lg ${imageSrc === placeholder ? "opacity-60" : ""
                            }`}
                          onError={(e) => (e.target.src = placeholder)}
                        />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="flex-1 flex flex-col justify-center min-w-0">
                            <h3 className="text-lg font-semibold text-white mb-2 truncate">
                              {vehicle.name || "Unknown Vehicle"}
                            </h3>

                            <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm mt-1">
                              <li className="flex items-center gap-1">
                                <span className="text-white/70">Lot No:</span>
                                <span className="font-medium text-description">{vehicle.lotNumber || "-"}</span>
                              </li>
                              <li className="hidden sm:block w-1 h-1 rounded-full bg-description"></li>
                              <li className="flex items-center gap-1">
                                <span className="text-white/70">Current Bid:</span>
                                <span className="font-semibold text-primary">
                                  {displayPriceWithSymbol(
                                    convertPrice(Number(vehicle.latestBid || vehicle.startingBid || 0))
                                  )}
                                </span>
                              </li>
                              <li className="hidden sm:block w-1 h-1 rounded-full bg-description"></li>
                              <li className="flex items-center gap-1">
                                <span className="text-white/70">Market Value:</span>
                                <span className="font-medium text-description">
                                  {displayPriceWithSymbol(
                                    convertPrice(Number(vehicle.marketValue || 0))
                                  )}
                                </span>
                              </li>
                              <li className="hidden sm:block w-1 h-1 rounded-full bg-description"></li>
                              <li className="flex items-center gap-1">
                                <span className="text-white/70">Auction:</span>
                                <span className="font-medium text-description">
                                  {vehicle.auctionLanesVehicles?.[0]?.auctionLane?.auction?.name || "-"}
                                </span>
                              </li>
                              {item.buyer?.showName && item.buyer?.user && (
                                <>
                                  <li className="hidden sm:block w-1 h-1 rounded-full bg-description"></li>
                                  <li className="flex items-center gap-1">
                                    <span className="text-white/70">Name:</span>
                                    <span className="font-medium text-description">
                                      {item.buyer.user.name || "-"} {item.buyer.user.clientId ? `(${item.buyer.user.clientId})` : ""}
                                    </span>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>

                          {/* Actions */}
                          {/* <div className="flex flex-row  gap-2 sm:gap-3 mt-3  justify-center items-center">
                            <button
                              onClick={() => navigate(`/${vehicle.slug}`)}
                              className="p-2 sm:p-2.5 text-blue-400 bg-blue-400/10 rounded hover:bg-blue-400/20 transition-colors"
                              title={translate("wishList.vehicle.viewDetails") || "View Details"}
                            >
                              <Eye className="w-5 h-5" />
                            </button>

                            <button
                              onClick={() => removeFromWishlist(vehicle.id)}
                              className="p-2 sm:p-2.5 text-red-500 bg-red-500/10 rounded hover:bg-red-500/20 transition-colors"
                              title={translate("wishList.vehicle.removeFromWishlist") || "Remove"}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div> */}
                          <div className="flex flex-row gap-2 sm:gap-3 mt-3 justify-center items-center">

                            {/* 1. VIEW DETAILS BUTTON */}
                            <div className="relative h-10 w-10">
                              <button
                                onClick={() => navigate(`/${vehicle.slug}`)}

                                className="absolute right-0 top-0 h-10 w-10 hover:w-40 flex items-center justify-end bg-primary  text-blue-600 rounded-lg transition-all duration-300 ease-in-out shadow-xl overflow-hidden z-10 hover:z-30 group"
                              >
                                <div className="flex flex-row-reverse items-center h-full w-full bg-primary group-hover:bg-primary-dark">
                                  {/* Icon Container */}
                                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                    <Eye size={20} className="text-white" />
                                  </div>
                                  {/* Text */}
                                  <span className="pl-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm  text-white font-semibold whitespace-nowrap">
                                    {translate("wishList.vehicle.viewDetails") || "View Details"}
                                  </span>
                                </div>
                              </button>
                            </div>

                            {/* 2. REMOVE BUTTON */}
                            <div className="relative h-10 w-10">
                              <button
                                onClick={() => removeFromWishlist(vehicle.id)}

                                className="absolute right-0 top-0 h-10 w-10 hover:w-56 flex items-center justify-end bg-red-50 border border-red-200 text-red-600 rounded-lg transition-all duration-300 ease-in-out shadow-xl overflow-hidden z-10 hover:z-30 group"
                              >
                                <div className="flex flex-row-reverse items-center h-full w-full bg-red-50 group-hover:bg-white">
                                  {/* Icon Container */}
                                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                                    <Trash2 size={20} />
                                  </div>
                                  {/* Text */}
                                  <span className="pl-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-semibold whitespace-nowrap">
                                    {translate("wishList.vehicle.removeFromWishlist") || "Remove"}
                                  </span>
                                </div>
                              </button>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
            <div className="text-sm text-description">
              Showing {(currentPage - 1) * pagination.limit + 1} to{" "}
              {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} items
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 border text-white hover:bg-white hover:text-black border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white hover:text-black ${currentPage === page
                      ? "bg-primary text-white"
                      : "border border-gray-300 hover:bg-white"
                      }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white text-white hover:text-black"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default WishList;